import { TEXT_SIZE_XSMALL, Txt, TxtProps } from 'components/Txt';
import { theme } from 'lib/theme';
import * as React from 'react';
import {
  AccessibilityProps,
  GestureResponderEvent,
  ImageStyle,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import { Icon, ICONS } from './Icon';
import { Pressable } from './Pressable';

export enum ButtonTheme {
  'primary' = 'primary',
  'secondary' = 'secondary',
  'themeless' = 'themeless',
  'link' = 'link',
  'header' = 'header',
}

export const BUTTON_BORDER_RADIUS = 4;
export const BUTTON_HEIGHT = 32;
export const BUTTON_COMPACT_HEIGHT = 24;

interface ButtonThemeConfig {
  adjustLineHeight: boolean;
  style: StyleProp<ViewStyle>;
  styleHovered: StyleProp<ViewStyle>;
  stylePressed: StyleProp<ViewStyle>;
  styleText: StyleProp<TextStyle>;
  iconColor: string;
  styleDestructive: StyleProp<ViewStyle>;
  styleHoveredDestructive: StyleProp<ViewStyle>;
  stylePressedDestructive: StyleProp<ViewStyle>;
  styleTextDestructive: StyleProp<TextStyle>;
  iconColorDestructive: string;
  height: number | undefined;
  heightCompact: number | undefined;
}

// const fixLineHeight = (lineHeight: number) =>
//   Platform.OS === 'ios'
//     ? lineHeight - 2
//     : Platform.OS === 'android'
//     ? lineHeight - 3
//     : lineHeight - 1;

const themes: Record<ButtonTheme, ButtonThemeConfig> = {
  link: {
    adjustLineHeight: false,
    height: undefined,
    heightCompact: undefined,
    style: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
      paddingHorizontal: theme.paddingHorizontal / 2,
    },
    styleHovered: {
      backgroundColor: theme.colors.blue10,
    },
    stylePressed: {
      backgroundColor: theme.colors.blue20,
    },
    styleText: {
      color: theme.colors.blue,
    },
    iconColor: theme.colors.blue,
    //
    styleDestructive: {},
    styleHoveredDestructive: {
      backgroundColor: theme.colors.red10,
    },
    stylePressedDestructive: {
      backgroundColor: theme.colors.red20,
    },
    styleTextDestructive: {
      color: theme.colors.red,
    },
    iconColorDestructive: theme.colors.red,
  },
  primary: {
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    style: {
      backgroundColor: theme.colors.blue,
      borderColor: theme.colors.blue80,
    },
    styleHovered: {
      backgroundColor: theme.colors.blue90,
    },
    stylePressed: {
      backgroundColor: theme.colors.blue80,
    },
    styleText: {
      color: 'white',
    },
    iconColor: 'white',
    //
    styleDestructive: {
      backgroundColor: theme.colors.red,
      borderColor: theme.colors.red80,
    },
    styleHoveredDestructive: {
      backgroundColor: theme.colors.red90,
    },
    stylePressedDestructive: {
      backgroundColor: theme.colors.red80,
    },
    styleTextDestructive: {
      color: 'white',
    },
    iconColorDestructive: 'white',
  },
  secondary: {
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    style: {
      backgroundColor: 'white',
      borderColor: theme.colors.blue80,
    },
    styleHovered: {
      backgroundColor: theme.colors.blue10,
    },
    stylePressed: {
      backgroundColor: theme.colors.blue20,
    },
    styleText: {
      color: theme.colors.blue,
    },
    iconColor: theme.colors.blue,
    //
    styleDestructive: {
      backgroundColor: 'white',
      borderColor: theme.colors.red80,
    },
    styleHoveredDestructive: {
      backgroundColor: theme.colors.red10,
    },
    stylePressedDestructive: {
      backgroundColor: theme.colors.red20,
    },
    styleTextDestructive: {
      color: theme.colors.red,
    },
    iconColorDestructive: theme.colors.red,
  },
  themeless: {
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    style: {
      backgroundColor: 'transparent',
      borderColor: 'transparent',
    },
    styleHovered: {
      backgroundColor: theme.colors.blue10,
    },
    stylePressed: {
      backgroundColor: theme.colors.blue20,
    },
    styleText: {
      color: theme.colors.blue,
    },
    iconColor: theme.colors.blue,
    //
    styleDestructive: {},
    styleHoveredDestructive: {
      backgroundColor: theme.colors.red10,
    },
    stylePressedDestructive: {
      backgroundColor: theme.colors.red20,
    },
    styleTextDestructive: {
      color: theme.colors.red,
    },
    iconColorDestructive: theme.colors.red,
  },
  header: {
    adjustLineHeight: true,
    height: BUTTON_HEIGHT,
    heightCompact: BUTTON_COMPACT_HEIGHT,
    style: {
      backgroundColor: theme.colors.blue5,
      borderColor: 'transparent',
    },
    styleHovered: {
      backgroundColor: theme.colors.blue10,
    },
    stylePressed: {
      backgroundColor: theme.colors.blue20,
    },
    styleText: {
      color: theme.colors.blue,
    },
    iconColor: theme.colors.blue,
    //
    styleDestructive: {},
    styleHoveredDestructive: {
      backgroundColor: theme.colors.red10,
    },
    stylePressedDestructive: {
      backgroundColor: theme.colors.red20,
    },
    styleTextDestructive: {
      color: theme.colors.red,
    },
    iconColorDestructive: theme.colors.red,
  },
};

export type ButtonProps = {
  onPress?: ((event: GestureResponderEvent) => void) | null;
  buttonTheme?: ButtonTheme;
  icon?: ICONS;
  iconSize?: number;
  iconStyle?: StyleProp<ImageStyle>;
  iconColor?: string;
  disabled?: boolean;
  chipLike?: boolean;
  iconRight?: ICONS;
  iconRightColor?: string;
  iconRightSize?: number;
  iconRightStyle?: StyleProp<ImageStyle>;
  textStyle?: StyleProp<TextStyle>;
  compact?: boolean;
  text?: string;
  textSize?: TxtProps['size'];
  textWeight?: TxtProps['weight'];
  uppercase?: boolean;
  marginRight?: boolean;
  marginLeft?: boolean;
  destructive?: boolean;
  focusOnRender?: boolean;
  /**
   * Overrides the text that's read by the screen reader when the user interacts with the element. By default, the
   * label is constructed by traversing all the children and accumulating all the Text nodes separated by space.
   */
  accessibilityLabel?: AccessibilityProps['accessibilityLabel'];
  /**
   * An accessibility hint helps users understand what will happen when they perform an action on the accessibility element when that result is not obvious from the accessibility label.
   */
  accessibilityHint?: AccessibilityProps['accessibilityHint'];
  adjustsFontSizeToFit?: boolean;
  inHeader?: boolean;
  numberOfLines?: number;
  style?: StyleProp<ViewStyle>;
  styleHovered?: StyleProp<ViewStyle>;
  stylePressed?: StyleProp<ViewStyle>;
  showBadge?: boolean;
  children?: React.ReactNode;
  focusable?: boolean;
  rbs?: boolean;
};

export const Button: React.FC<ButtonProps> = ({
  buttonTheme = ButtonTheme.primary,
  children,
  chipLike,
  compact = false,
  destructive,
  disabled,
  focusOnRender,
  icon,
  iconSize = 19,
  iconRight,
  iconRightSize,
  iconRightStyle,
  iconStyle,
  iconColor,
  iconRightColor,
  marginLeft = false,
  marginRight = false,
  onPress,
  text,
  textSize,
  textStyle,
  textWeight = 'bold',
  uppercase = false,
  accessibilityLabel,
  accessibilityHint,
  adjustsFontSizeToFit,
  inHeader,
  numberOfLines = 1,
  style,
  styleHovered,
  stylePressed,
  showBadge,
  focusable,
  rbs,
}) => {
  const themeConfig = themes[buttonTheme];
  const buttonText = children || text || undefined;
  return (
    <Pressable
      focusable={focusable}
      accessible={focusable}
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      focusOnRender={focusOnRender}
      onPress={onPress || undefined}
      disabled={disabled}
      style={[
        styles.button,
        inHeader ? styles.headerButton : undefined,
        !disabled && onPress ? styles.buttonPressable : undefined,
        compact ? styles.buttonCompact : undefined,
        chipLike
          ? compact
            ? styles.buttonChipLikeCompact
            : styles.buttonChipLike
          : undefined,
        themeConfig.style,
        { height: compact ? themeConfig.heightCompact : themeConfig.height },
        destructive ? themeConfig.styleDestructive : undefined,
        disabled || !onPress ? styles.disabled : undefined,
        marginRight ? { marginRight: theme.buttonMargin } : undefined,
        marginLeft ? { marginLeft: theme.buttonMargin } : undefined,
        style,
      ]}
      hoveredStyle={[
        themeConfig.styleHovered,
        destructive ? themeConfig.styleHoveredDestructive : undefined,
        styleHovered,
      ]}
      pressedStyle={[
        themeConfig.stylePressed,
        destructive ? themeConfig.stylePressedDestructive : undefined,
        stylePressed,
      ]}
    >
      {rbs && (
        <View
          style={{
            backgroundColor: theme.colors.support30,
            width: 4,
            height: 4,
            borderRadius: 2,
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 9,
          }}
        />
      )}
      {icon && (
        <Icon
          source={icon}
          size={iconSize}
          color={
            iconColor ||
            (destructive
              ? themeConfig.iconColorDestructive
              : themeConfig.iconColor)
          }
          style={iconStyle}
        />
      )}
      {!!buttonText && (
        <Txt
          adjustsFontSizeToFit={adjustsFontSizeToFit}
          size={textSize}
          weight={textWeight}
          style={[
            compact ? styles.textCompact : styles.text,
            themeConfig.adjustLineHeight
              ? compact
                ? styles.textCompactLineHeight
                : styles.textLineHeight
              : undefined,
            icon ? styles.textWithIcon : null,
            themeConfig.styleText,
            destructive ? themeConfig.styleTextDestructive : undefined,
            uppercase ? { textTransform: 'uppercase' } : undefined,
            textStyle,
          ]}
          numberOfLines={numberOfLines}
        >
          {buttonText}
        </Txt>
      )}
      {iconRight && (
        <Icon
          source={iconRight}
          size={iconRightSize || 19}
          color={
            iconRightColor ||
            (destructive
              ? themeConfig.iconColorDestructive
              : themeConfig.iconColor)
          }
          style={[{ marginLeft: theme.paddingHorizontalHalf }, iconRightStyle]}
        />
      )}
      {showBadge && (
        <View
          style={{
            position: 'absolute',
            right: 2,
            bottom: 2,
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.colors.orange,
            zIndex: 1,
            elevation: 1,
          }}
        />
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    borderRadius: BUTTON_BORDER_RADIUS,
    borderWidth: 1,
    flexDirection: 'row',
    paddingHorizontal: theme.paddingHorizontal,
  },
  headerButton: {
    paddingHorizontal: theme.paddingHorizontal / 2,
  },
  buttonChipLike: {
    borderRadius: 999,
  },
  buttonChipLikeCompact: {
    borderRadius: 999,
  },
  buttonCompact: {
    height: BUTTON_COMPACT_HEIGHT,
    paddingHorizontal: theme.paddingHorizontal / 2,
  },
  buttonPressable: {
    alignItems: 'center',
    ...Platform.select({
      web: { cursor: 'pointer' },
    }),
  },
  disabled: {
    opacity: 0.42,
  },
  text: {
    textAlign: 'center',
  },
  textCompact: {
    textAlign: 'center',
    fontSize: TEXT_SIZE_XSMALL,
  },
  textLineHeight: {
    includeFontPadding: false,
    marginTop: Platform.OS === 'web' ? 1 : 0,
  },
  textCompactLineHeight: {
    includeFontPadding: false,
  },
  textWithIcon: {
    marginLeft: theme.grid / 2,
  },
});
